import { useEffect, useState } from "react";
import { DollarSign, ShoppingBag, PackageCheck, TrendingUp, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { adminApi } from "@/lib/adminApi";
import { fmt, fmtDate, StatusBadge, Card, Spinner } from "@/components/admin/shared";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <Card className="!p-5">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-full ${accent ? "bg-[#F4A97F] text-white" : "bg-[#EFE7D6] text-[#FBC4A8]"}`}><Icon className="h-4 w-4" /></span>
    </div>
    <p className="mt-3 font-display text-2xl text-[#2C2B2C]">{value}</p>
    <p className="mt-0.5 text-xs uppercase tracking-wider text-[#8A7F86]">{label}</p>
  </Card>
);

export function DashboardHome({ onGoOrders }) {
  const [d, setD] = useState(null);

  useEffect(() => { adminApi.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;

  return (
    <div data-testid="dashboard-home" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Home</h1>
        <p className="mt-1 text-sm text-[#8A7F86]">Here's how Aura Glow is doing today.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={DollarSign} label="Today's sales" value={fmt(d.today_sales)} accent />
        <Stat icon={ShoppingBag} label="Today's orders" value={d.today_orders} />
        <button onClick={onGoOrders} data-testid="dash-unfulfilled" className="text-left">
          <Card className="!p-5 transition-colors hover:border-[#FBC4A8]">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0E7E0] text-[#B07A4E]"><PackageCheck className="h-4 w-4" /></span>
            <p className="mt-3 font-display text-2xl text-[#2C2B2C]">{d.unfulfilled_count}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs uppercase tracking-wider text-[#8A7F86]">Orders to fulfil <ArrowRight className="h-3 w-3" /></p>
          </Card>
        </button>
        <Stat icon={TrendingUp} label="Avg order value" value={fmt(d.aov)} />
      </div>

      <Card>
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-lg text-[#2C2B2C]">Sales · last 7 days</h3>
          <span className="text-sm text-[#8A7F86]">Total revenue <span className="font-semibold text-[#2C2B2C]">{fmt(d.total_revenue)}</span></span>
        </div>
        <div className="mt-4 h-60">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={d.sales_series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FBC4A8" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FBC4A8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F2EAE3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A7F86" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#8A7F86" }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="sales" stroke="#F4A97F" strokeWidth={2} fill="url(#salesFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
